import React, { useCallback, useState } from "react";
import PropTypes from "prop-types";
import { FormattedMessage, useIntl, defineMessages } from "react-intl";
import { ToastTip } from "./ToastTip";
import { useEffect } from "react";
import { ReactComponent as ShiftIcon } from "../icons/ShiftKey.svg";
import { ReactComponent as KeyQIcon } from "../icons/Rotate_Left.svg";
import { ReactComponent as KeyEIcon } from "../icons/Rotate_Right.svg";
import { ReactComponent as TouchAndDrag } from "../icons/LookAroundMobile.svg";
import { ReactComponent as Joystick } from "../icons/Joystick.svg";
import { ReactComponent as ArrowsIcon } from "../icons/Arrows.svg";
import { ReactComponent as WasdIcon } from "../icons/Wasd.svg";
import { ReactComponent as MouseRotate } from "../icons/MouseRotate.svg";
import { ReactComponent as MouseTeleport } from "../icons/MouseTeleport.svg";
import { ReactComponent as MouseInteract } from "../icons/MouseInteract.svg";
import { ReactComponent as MobileInteract } from "../icons/MobileInteract.svg";
import { ReactComponent as KeyGIcon } from "../icons/G.svg";

import { discordBridgesForPresences, hasEmbedPresences } from "../../utils/phoenix-utils";
import configs from "../../utils/configs";
import styles from "./Tip.scss";

import { FluentTips } from "./FluentTips";
import { useParticipantLayoutActive } from "../layout/participant-layout/ParticipantLayoutContext";

// These keys are hardcoded in the input system to be based on the physical location on the keyboard rather than character
let moveKeys = "W A S D";
let turnLeftKey = "Q";
let turnRightKey = "E";
let flyKey = "G";

// TODO The API to map from physical key to character is experimental. Depending on prospects of this getting wider
// implimentation we may want to cook up our own polyfill based on observing key inputs
if (window.navigator.keyboard !== undefined && window.navigator.keyboard.getLayoutMap) {
  window.navigator.keyboard
    .getLayoutMap()
    .then(function (map) {
      moveKeys = `${map.get("KeyW") || "W"}, ${map.get("KeyA") || "A"}, ${map.get("KeyS") || "S"}, ${
        map.get("KeyD") || "D"
      }`.toUpperCase();
      turnLeftKey = map.get("KeyQ")?.toUpperCase();
      turnRightKey = map.get("KeyE")?.toUpperCase();
      flyKey = map.get("KeyG")?.toUpperCase();
    })
    .catch(function (e) {
      // This occurs on Chrome 93 when the Hubs page is in an iframe
      console.warn(`Unable to remap keyboard: ${e}`);
    });
}

const headerMessages = {
  "tips.desktop.intro": {
    id: "tips.desktop.intro-title",
    header: "Hello!",
    buttonStart: true,
    activeButtonLabel: {
      id: "tips.button.got-it-action-action",
      header: "Show me"
    }
    // dontShowAgainButton: true,
    // dontShowAgainLabel: {
    //   id: "tips.button.dont-show-again",
    //   header: "Don't show again"
    // }
  },
  "tips.mobile.intro": {
    id: "tips.mobile.intro-title",
    header: "Hello!",
    buttonStart: true,
    activeButtonLabel: {
      id: "tips.button.got-it-action",
      header: "Show me"
    }
    // dontShowAgainButton: true,
    // dontShowAgainLabel: {
    //   id: "tips.button.dont-show-again",
    //   header: "Don't show again"
    // }
  },
  "tips.desktop.cIntro": {
    id: "tips.c-intro-title",
    header: "Discover More Features!",
    buttonStart: true,
    activeButtonLabel: {
      id: "tips.button.got-it-action",
      header: "Show me"
    }
    // dontShowAgainButton: true,
    // dontShowAgainLabel: {
    //   id: "tips.button.dont-show-again",
    //   header: "Don't show again"
    // }
  },
  "tips.mobile.cIntro": {
    id: "tips.c-intro-title",
    header: "Discover More Features!",
    buttonStart: true,
    activeButtonLabel: {
      id: "tips.button.got-it-action",
      header: "Show me"
    }
    // dontShowAgainButton: true,
    // dontShowAgainLabel: {
    //   id: "tips.button.dont-show-again",
    //   header: "Don't show again"
    // }
  },
  "tips.desktop.locomotion": {
    id: "tips.desktop.locomotion-title",
    header: "Move",
    position: 1,
    buttonStart: true,
    activeButtonLabel: {
      id: "tips.button.next",
      header: "Next"
    }
  },
  "tips.mobile.locomotion": {
    id: "tips.mobile.locomotion-title",
    header: "Move",
    position: 1,
    buttonStart: true,
    activeButtonLabel: {
      id: "tips.button.next",
      header: "Next"
    }
  },
  "tips.desktop.locomotionBoost": {
    id: "tips.desktop.locomotionBoost",
    header: "Move Faster",
    position: 2,
    buttonStart: true,
    activeButtonLabel: {
      id: "tips.button.next",
      header: "Next"
    }
  },
  "tips.desktop.turning": {
    id: "tips.desktop.turning-title",
    header: "Turn",
    position: 3,
    buttonStart: true,
    activeButtonLabel: {
      id: "tips.button.next",
      header: "Next"
    }
  },
  "tips.desktop.interact": {
    id: "tips.desktop.interact",
    header: "Interact",
    position: 5,
    buttonStart: true,
    activeButtonLabel: {
      id: "tips.button.next",
      header: "Next"
    }
  },
  "tips.mobile.interact": {
    id: "tips.mobile.interact",
    header: "Interact",
    position: 3,
    buttonStart: true,
    activeButtonLabel: {
      id: "tips.button.next",
      header: "Next"
    }
  },
  "tips.mobile.look": {
    id: "tips.mobile.look",
    header: "Look Around",
    position: 2,
    buttonStart: true,
    activeButtonLabel: {
      id: "tips.button.next",
      header: "Next"
    }
  },
  "tips.mobile.invite": {
    position: 3
  },
  "tips.desktop.teleport": {
    id: "tips.desktop.teleport-title",
    header: "Teleport",
    position: 4,
    buttonStart: true,
    activeButtonLabel: {
      id: "tips.button.next",
      header: "Next"
    }
  },
  "tips.desktop.fly": {
    id: "tips.desktop.fly-title",
    header: "Fly",
    position: 6,
    buttonStart: true,
    activeButtonLabel: {
      id: "tips.button.next",
      header: "Next"
    }
  },
  "tips.desktop.end": {
    id: "tips.desktop.end-title",
    header: "Well Done!",
    position: 7,
    buttonStart: true,
    hideSkipButton: true,
    activeButtonLabel: {
      id: "tips.button.done",
      header: "Done"
    },
    restartButton: true,
    restartLabel: {
      id: "tips.button.restart-tour",
      header: "Restart Tour"
    }
  },
  "tips.mobile.end": {
    id: "tips.mobile.end-title",
    position: 4,
    header: "Well Done!",
    buttonStart: true,
    hideSkipButton: true,
    activeButtonLabel: {
      id: "tips.button.done",
      header: "Done"
    },
    restartButton: true,
    restartLabel: {
      id: "tips.button.restart-tour",
      header: "Restart Tour"
    }
  },
  "tips.desktop.upload": {
    id: "tips.desktop.upload",
    header: "How to Upload an Asset?",
    position: 1,
    buttonStart: true,
    activeButtonLabel: {
      id: "tips.button.next",
      header: "Next"
    },
    videoId: "LZqOHbbVZSw"
  },
  "tips.mobile.upload": {
    id: "tips.mobile.upload",
    header: "How to Upload an Asset?",
    position: 1,
    buttonStart: true,
    activeButtonLabel: {
      id: "tips.button.next",
      header: "Next"
    },
    videoId: "LZqOHbbVZSw"
  },
  "tips.desktop.import": {
    id: "tips.desktop.import",
    header: "How to Import an Asset?",
    position: 2,
    buttonStart: true,
    activeButtonLabel: {
      id: "tips.button.next",
      header: "Next"
    },
    videoId: "3pTxNcfCXvs"
  },
  "tips.mobile.import": {
    id: "tips.mobile.import",
    header: "How to Import an Asset?",
    position: 2,
    buttonStart: true,
    activeButtonLabel: {
      id: "tips.button.next",
      header: "Next"
    },
    videoId: "3pTxNcfCXvs"
  },
  "tips.desktop.pin": {
    id: "tips.desktop.pin",
    header: "How to make an element persistent?",
    position: 3,
    buttonStart: true,
    activeButtonLabel: {
      id: "tips.button.next",
      header: "Done"
    },
    videoId: "2Ua4TzffsS8"
  },
  "tips.mobile.pin": {
    id: "tips.mobile.pin",
    header: "How to make an element persistent?",
    position: 3,
    buttonStart: true,
    activeButtonLabel: {
      id: "tips.button.next",
      header: "Done"
    },
    videoId: "2Ua4TzffsS8"
  }
};

// Icon size constants based on SVG files
const ICON_SIZES = {
  ShiftIcon: { width: 65, height: 40 },
  KeyQIcon: { width: 28, height: 30 },
  KeyEIcon: { width: 28, height: 30 },
  TouchAndDrag: { width: 38, height: 51 },
  Joystick: { width: 51, height: 51 },
  ArrowsIcon: { width: 80, height: 56 },
  WasdIcon: { width: 81, height: 56 },
  MouseRotate: { width: 78, height: 50 },
  MouseTeleport: { width: 33, height: 52 },
  MouseInteract: { width: 32, height: 51 },
  MobileInteract: { width: 36, height: 51 },
  KeyGIcon: { width: 39, height: 42 }
};

const desktopControlContent = () => {
  return (
    <div className={styles.iconsContainer}>
      <ArrowsIcon style={ICON_SIZES.ArrowsIcon} />
      <WasdIcon style={ICON_SIZES.WasdIcon} />
    </div>
  );
};

const arrowKeys = () => {
  return (
    <span className={styles.semibold}>
      <FormattedMessage id="tips.controls.arrow-keys" defaultMessage="Arrow keys" />
    </span>
  );
};

const WASD = () => {
  return <span className={styles.semibold}>{moveKeys}</span>;
};

const desktopShiftContent = () => {
  return (
    <div className={styles.iconsContainer}>
      <ShiftIcon style={ICON_SIZES.ShiftIcon} />
    </div>
  );
};

const desktopQEKeyContent = () => {
  return (
    <div className={styles.iconsContainer}>
      <KeyQIcon style={ICON_SIZES.KeyQIcon} />
      <KeyEIcon style={ICON_SIZES.KeyEIcon} />
    </div>
  );
};

const desktopMouseRotateContent = () => {
  return (
    <div className={styles.iconsContainer}>
      <MouseRotate style={ICON_SIZES.MouseRotate} />
    </div>
  );
};

const desktopLookAroundContent = () => {
  return (
    <div className={styles.iconsContainer}>
      <MouseInteract style={ICON_SIZES.MouseInteract} />
    </div>
  );
};

const mobileInteractContent = () => {
  return (
    <div className={styles.iconsContainer}>
      <MobileInteract style={ICON_SIZES.MobileInteract} />
    </div>
  );
};

const desktopTeleportContent = () => {
  return (
    <div className={styles.iconsContainer}>
      <MouseTeleport style={ICON_SIZES.MouseTeleport} />
    </div>
  );
};

const desktopGKeyContent = () => {
  return (
    <div className={styles.iconsContainer}>
      <KeyGIcon style={ICON_SIZES.KeyGIcon} />
    </div>
  );
};

const mobileLookContent = () => {
  return (
    <div className={styles.iconsContainer}>
      <TouchAndDrag style={ICON_SIZES.TouchAndDrag} />
    </div>
  );
};

const mobileLocomotionContent = () => {
  return (
    <div className={styles.iconsContainer} style={{ gap: "15px" }}>
      <Joystick style={ICON_SIZES.Joystick} />
      <Joystick style={ICON_SIZES.Joystick} />
    </div>
  );
};

const onboardingMessages = defineMessages({
  "tips.mobile.look": {
    id: "tips.mobile.look",
    defaultMessage: "{mobileLookContent} Use Touch + Drag to rotate the camera.."
  },
  "tips.mobile.locomotion": {
    id: "tips.mobile.locomotion",
    defaultMessage: "{mobileLocomotionContent} Use the Joystick to move around the Verse.."
  },
  "tips.mobile.invite": {
    id: "tips.mobile.invite",
    defaultMessage: "Use the Invite button in the bottom left to share this room."
  },
  "tips.mobile.intro": {
    id: "tips.desktop.intro",
    defaultMessage: "Welcome to the metaverse. Let's quickly review the main features. Ready to get started?"
  },
  "tips.desktop.intro": {
    id: "tips.desktop.intro",
    defaultMessage: "Welcome to the metaverse. Let's quickly review the main features. Ready to get started?"
  },
  "tips.desktop.cIntro": {
    id: "tips.desktop.c-intro",
    defaultMessage:
      "As a collaborator, we encourage you to discover some of the Metaverse features that you might find useful. Ready to learn them?"
  },
  "tips.mobile.cIntro": {
    id: "tips.mobile.c-intro",
    defaultMessage:
      "As a collaborator, we encourage you to discover some of the Metaverse features that you might find useful. Ready to learn them?"
  },
  "tips.desktop.end": {
    id: "tips.desktop.end",
    defaultMessage: "You're now ready to explore our Verses."
  },
  "tips.mobile.end": {
    id: "tips.desktop.end",
    defaultMessage: "You're now ready to explore our Verses."
  },
  "tips.desktop.interact": {
    id: "tips.desktop.interact",
    defaultMessage: "{desktopLookAroundContent} Use the Left Mouse button to interact with elements in the Verse."
  },
  "tips.mobile.interact": {
    id: "tips.mobile.interact",
    defaultMessage: "{mobileInteractContent} Use Touch to interact with the elements of the Verse."
  },
  "tips.desktop.locomotion": {
    id: "tips.desktop.locomotion-description",
    defaultMessage: "{desktopControlContent} Use the Arrow Keys or {moveKeys} to move around the Verse."
  },
  "tips.desktop.turning": {
    id: "tips.desktop.turning",
    defaultMessage:
      "{desktopMouseRotateContent} {desktopQEKeyContent} Use the {turnLeftKey} and {turnRightKey} keys or the left mouse button to rotate the camera."
  },
  "tips.desktop.invite": {
    id: "tips.desktop.invite",
    defaultMessage: "Nobody else is here. Use the invite button in the bottom left to share this room."
  },
  "tips.desktop.teleport": {
    id: "tips.desktop.teleport-description",
    defaultMessage:
      "{desktopTeleportContent} Long press the Right Mouse button to quickly teleport to any space in the verse."
  },
  "tips.desktop.fly": {
    id: "tips.desktop.fly",
    defaultMessage: "{desktopGKeyContent} Use the {flyKey} key to fly up in the Verse."
  },
  "tips.c-intro": {
    id: "tips.c-intro",
    defaultMessage:
      "As a collaborator, we encourage you to discover some of the Metaverse features that you might find useful. Ready to learn them?"
  },
  "tips.desktop.upload": {
    id: "tips.desktop.upload",
    defaultMessage:
      "To upload an asset, open the More modal (the three-dot icon), select Upload, then click File. Choose a file, add details, and click Save to store it in Files and place it in the Verse."
  },
  "tips.mobile.upload": {
    id: "tips.mobile.upload",
    defaultMessage:
      "To upload an asset, open the More modal (the three-dot icon), select Upload, then click File. Choose a file, add details, and click Save to store it in Files and place it in the Verse."
  },
  "tips.desktop.import": {
    id: "tips.desktop.import",
    defaultMessage:
      "To import a library asset in the Verse, open the More modal, select Assets, and browse the Public Domain Art or Sketchfab tabs. Choose an asset, then click Place to add it to the scene."
  },
  "tips.mobile.import": {
    id: "tips.mobile.import",
    defaultMessage:
      "To import a library asset in the Verse, open the More modal, select Assets, and browse the Public Domain Art or Sketchfab tabs. Choose an asset, then click Place to add it to the scene."
  },
  "tips.desktop.pin": {
    id: "tips.desktop.pin",
    defaultMessage:
      "To lock an asset in place, long-press by clicking while hovering over the object. When the menu opens, select Lock (pushpin icon). This prevents repositioning and ensures it remains in the Verse when you leave."
  },
  "tips.mobile.pin": {
    id: "tips.mobile.pin",
    defaultMessage:
      "To lock an asset in place, long-press by clicking while hovering over the object. When the menu opens, select Lock (pushpin icon). This prevents repositioning and ensures it remains in the Verse when you leave."
  },
  "tips.desktop.locomotionBoost": {
    id: "tips.desktop.locomotionBoost",
    defaultMessage: "{desktopShiftContent} Use W, A, S, D + hold Shift to move quickly through the Verse."
  }
});

function OkDismissLabel() {
  return <FormattedMessage id="tips.dismiss.ok" defaultMessage="Ok" />;
}

export function FullscreenTip(props) {
  return (
    <FluentTips {...props} dismissLabel={<OkDismissLabel />}>
      <FormattedMessage id="tips.fullscreen" defaultMessage="Entered fullscreen mode. Press Escape to show UI." />
    </FluentTips>
  );
}

export function RecordModeTip() {
  return (
    <ToastTip>
      <FormattedMessage id="record-mode-enabled-tip" defaultMessage="Record mode on, press 'B' to toggle off" />
    </ToastTip>
  );
}

function SkipDismissLabel() {
  return <FormattedMessage id="tips.dismiss.skip-action" defaultMessage="Skip" />;
}

const isMobile = AFRAME.utils.device.isMobile();

export function TipContainer({
  inLobby = false,
  inRoom,
  isStreaming,
  isEmbedded,
  scene,
  store,
  hubId,
  presences,
  participantLayoutActive: participantLayoutActiveProp
}) {
  const [lobbyTipDismissed, setLobbyTipDismissed] = useState(false);
  const [broadcastTipDismissed, setBroadcastTipDismissed] = useState(() =>
    store.state.confirmedBroadcastedRooms.includes(hubId)
  );
  const [streamingTipDismissed, setStreamingTipDismissed] = useState(false);
  const [embeddedTipDismissed, setEmbeddedTipDismissed] = useState(false);
  const [onboardingTipId, setOnboardingTipId] = useState(null);
  const contextParticipantLayoutActive = useParticipantLayoutActive();
  const participantLayoutActive =
    participantLayoutActiveProp === undefined ? contextParticipantLayoutActive : participantLayoutActiveProp;

  const intl = useIntl();

  const onSkipOnboarding = useCallback(() => {
    scene.systems.tips.skipTips();
  }, [scene]);

  useEffect(() => {
    function onSceneTipChanged({ detail: tipId }) {
      setOnboardingTipId(null);
      setOnboardingTipId(tipId);
    }

    scene.addEventListener("tip-changed", onSceneTipChanged);

    setOnboardingTipId(scene.systems.tips.activeTip);
  }, [scene]);

  const discordBridges = presences ? discordBridgesForPresences(presences) : [];
  const isBroadcasting = discordBridges.length > 0;

  // Get the current tips array and calculate the correct active step
  const currentTips = scene.systems.tips.getTips();
  let activeStep = undefined;

  if (onboardingTipId) {
    // Extract the tip name from the onboardingTipId (e.g., "tips.desktop.intro" -> "intro")
    const tipName = onboardingTipId.split(".").pop();
    // Find the actual position in the filtered tips array
    activeStep = currentTips.indexOf(tipName) + 1; // +1 because positions are 1-based
  }

  const onSetTipSelected = () => {
    if (activeStep) {
      scene.systems.tips.skipTip(activeStep - 1); // -1 because skipTip expects 0-based index
    } else {
      scene.systems.tips.skipTip(0);
    }
  };

  const onRestartTour = scene => {
    scene.systems.tips.resetTips();
  };

  const onDontShowAgainChange = e => {
    if (e.target.checked) {
      // TO DO: Commit in the database that the user doesn't want to see this tip again
      localStorage.setItem("tips.dontShowAgain", true);
    } else {
      localStorage.removeItem("tips.dontShowAgain");
    }
  };

  if (inLobby) {
    if (lobbyTipDismissed) {
      return null;
    }

    return (
      <FluentTips onDismiss={() => setLobbyTipDismissed(true)} dismissLabel={<OkDismissLabel />}>
        <FormattedMessage id="tips.lobby" defaultMessage="You're in the lobby. Others cannot see or hear you." />
      </FluentTips>
    );
  } else if (inRoom) {
    if (onboardingTipId && !participantLayoutActive) {
      return (
        <FluentTips
          onDismiss={onSkipOnboarding}
          step={scene.systems.tips.getTips()}
          activeStep={activeStep}
          onSetTipSelected={onSetTipSelected}
          dontShowAgain={headerMessages[onboardingTipId].dontShowAgainButton}
          dontShowAgainLabel={
            headerMessages[onboardingTipId].dontShowAgainButton
              ? headerMessages[onboardingTipId].dontShowAgainLabel
              : undefined
          }
          onDontShowAgainChange={onDontShowAgainChange}
          restartButton={headerMessages[onboardingTipId].restartButton}
          onRestartTour={() => onRestartTour(scene)}
          buttonRestart={headerMessages[onboardingTipId].restartButton}
          activeButtonLabel={
            headerMessages[onboardingTipId] ? headerMessages[onboardingTipId].activeButtonLabel : undefined
          }
          buttonStart={headerMessages[onboardingTipId] ? headerMessages[onboardingTipId].buttonStart : undefined}
          hideSkipButton={headerMessages[onboardingTipId] ? headerMessages[onboardingTipId].hideSkipButton : undefined}
          dismissLabel={<SkipDismissLabel />}
          videoId={headerMessages[onboardingTipId].videoId}
          header={
            <>
              {headerMessages[onboardingTipId].id && (
                <>
                  {intl.formatMessage({
                    id: headerMessages[onboardingTipId].id,
                    defaultMessage: headerMessages[onboardingTipId].header
                  })}
                </>
              )}
            </>
          }
          content={
            <>
              {intl.formatMessage(onboardingMessages[onboardingTipId], {
                appName: configs.translation("app-name"),
                moveKeys,
                desktopControlContent: desktopControlContent(),
                arrowKeys: arrowKeys(),
                WASD: WASD(),
                desktopShiftContent: desktopShiftContent(),
                desktopMouseRotateContent: desktopMouseRotateContent(),
                desktopQEKeyContent: desktopQEKeyContent(),
                desktopLookAroundContent: desktopLookAroundContent(),
                desktopTeleportContent: desktopTeleportContent(),
                desktopGKeyContent: desktopGKeyContent(),
                mobileLookContent: mobileLookContent(),
                mobileLocomotionContent: mobileLocomotionContent(),
                mobileInteractContent: mobileInteractContent(),
                turnLeftKey,
                turnRightKey,
                flyKey
              })}
            </>
          }
          isMobile={isMobile}
        ></FluentTips>
      );
    }

    if (isStreaming && !streamingTipDismissed) {
      return (
        <FluentTips onDismiss={() => setStreamingTipDismissed(true)} dismissLabel={<OkDismissLabel />}>
          <FormattedMessage
            id="tips.streaming"
            defaultMessage="Now broadcasting to the lobby. Exit streamer mode in the add menu when you're done."
          />
        </FluentTips>
      );
    }

    if (isBroadcasting && !broadcastTipDismissed) {
      return (
        <FluentTips onDismiss={() => setBroadcastTipDismissed(true)} dismissLabel={<OkDismissLabel />}>
          <FormattedMessage
            id="tips.discord"
            defaultMessage="Chat in this room is being bridged to {broadcastTarget} on Discord."
            values={{ broadcastTarget: discordBridges.map(channelName => "#" + channelName).join(", ") }}
          />
        </FluentTips>
      );
    }

    if ((isEmbedded || hasEmbedPresences(presences)) && !embeddedTipDismissed) {
      return (
        <FluentTips onDismiss={() => setEmbeddedTipDismissed(true)} dismissLabel={<OkDismissLabel />}>
          <FormattedMessage
            id="tips.embedded"
            defaultMessage="This Verse is embedded, so it may be visible to visitors on other websites."
          />
        </FluentTips>
      );
    }

    return null;
  }

  return null;
}

TipContainer.propTypes = {
  inLobby: PropTypes.bool,
  inRoom: PropTypes.bool,
  isStreaming: PropTypes.bool,
  isEmbedded: PropTypes.bool,
  scene: PropTypes.object.isRequired,
  store: PropTypes.object.isRequired,
  hubId: PropTypes.string,
  presences: PropTypes.object,
  participantLayoutActive: PropTypes.bool
};
